/* eslint-disable import/no-cycle */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useQuery } from "@apollo/client";
import ReactDOM from "react-dom";
import * as userActions from "../../../redux/actions/user";
import * as elementsActions from "../../../redux/actions/elements";
import { ME_HEADER_QUERY } from "../../../graphql/user";
import { amplitudeDeliveryWidgetPageSection } from "../../../shared/constants/amplitudeDeliveryWidgetPageSections";
import { orderMethods, orderTypeIds } from "../../../shared/constants/order";
import * as storeRepo from "../../../shared/repos/graphql/store";
import useProminentNavigation from "../../ExperimentGate/useProminentNavigation";
import NavigationBar from "./NavigationBar/NavigationBar";
import MobileMenu from "./HamburgerMenu/FullScreenHomepageHamburgerMenu";
import DeliveryPickUpWidget from "../../DeliveryPickUpWidget/DeliveryPickUpWidget";
import BarcodeWidget from "../../BarcodeWidget/BarcodeWidget";
import RibbonBanner from "../../RibbonBanner/RibbonBanner";
import CookieToast from "../CookieToast";
import ShippingReturnBar from "./ShippingReturnBar/ShippingReturnBar";
import styles from "./FullScreenHomepageHeader.module.css";
import "./FullScreenHomepageHeaderBrandRefresh.module.css";
import { isBrandRefreshEnabled } from "../../../shared/utilities/brandRefresh";
import { PUNCHH } from "../../../shared/constants/loyaltyType";
import { loadPunchhLoyalty } from "../../../shared/utilities/loadPunchhLoyalty";
import { getHeaderHomepageProps, getHeaderRegionProps } from "./fullScreenHomepageHeaderBrandRefreshClasses";
import { getAddedCartLines, getCartLineQuantity } from "../../../shared/utilities/orders";

export const FullScreenHomepageHeaderComponent = ({
  location,
  showHeaderWidget,
  headerWidgetSource,
  showDeliveryPickupAddressBar,
  children,
  userToken,
  setUserInfo,
  setUserId,
  showMobileMenu,
  toggleMobileMenu,
  hideHeaderWidget,
  userCart,
  selectedStore,
  userAddress,
  enableDefaultAddressForGuestUsers,
  guestSelectedStore,
  guestUserOrderMethod,
  guestUserAddress,
  setSelectedStore,
  setUserOrderMethod,
  setUserAddress,
  setBetaCustomer,
  setPermissions,
  hideHeaderMenuOnScroll,
  userOrderMethod,
  showDeliveryPickupWidget,
  orderProducts,
  hasActiveOrders,
  noActiveOrders,
  activeLoyalty,
  order,
}) => {
  const prominentNavigation = useProminentNavigation();
  const isMenuPage = location.pathname.includes("/menu");
  const addressType =
    userOrderMethod === orderMethods.pickup ? "pickup" : "delivery";
  const [showNotification, setShowNotification] = useState(false);
  const [amountAdded, setAmountAdded] = useState(0);
  const [productAddedImagePath, setProductAddedImagePath] = useState(null);
  const prevCartRef = React.useRef({ lines: [], qty: 0 });
  const isInitialMount = React.useRef(true);
  const notificationTimeoutRef = React.useRef(null);
  const [hideNavigationBar, setHideNavigationBar] = useState(false);
  const useFullscreenCarousel = isBrandRefreshEnabled();

  useEffect(() => {
    if (userToken && activeLoyalty === PUNCHH) {
      loadPunchhLoyalty(order);
    }
    // order intentionally omitted — match legacy Header (bootstrap on auth/loyalty only)
  }, [userToken, activeLoyalty]);

  useEffect(() => {
    const currentCart = userCart || [];
    const currentQty = getCartLineQuantity(userCart);

    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevCartRef.current = { lines: currentCart, qty: currentQty };
      return undefined;
    }

    const previousCart = prevCartRef.current.lines || [];
    const previousQty = prevCartRef.current.qty || 0;

    if (currentQty > previousQty) {
      const newAddedProducts = getAddedCartLines(currentCart, previousCart);
      const newAddedProductImage =
        newAddedProducts.length > 0
          ? newAddedProducts[0].product?.productImageSmall ||
            newAddedProducts[0].product?.productImage ||
            null
          : null;

      setAmountAdded(currentQty - previousQty);
      setProductAddedImagePath(newAddedProductImage);
      setShowNotification(true);

      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      notificationTimeoutRef.current = setTimeout(() => {
        setShowNotification(false);
        notificationTimeoutRef.current = null;
      }, 4000);
    }

    prevCartRef.current = { lines: currentCart, qty: currentQty };
    return undefined;
  }, [userCart]);

  useEffect(
    () => () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    },
    [],
  );

  useQuery(ME_HEADER_QUERY, {
    skip: !userToken,
    fetchPolicy: "no-cache",
    onCompleted: async (res) => {
      if (res?.me) {
        const user = {
          firstName: res.me.firstname,
          lastName: res.me.lastname,
        };
        setUserInfo({ user });
        setUserId(res.me.id);
        setBetaCustomer(res.me.isBetaCustomer);
        setPermissions(res.me.permissions || []);

        if (res.me.activeOrders?.totalCount > 0) {
          hasActiveOrders();
        } else {
          noActiveOrders();
        }

        if (
          res.me.lastCompletedOrder?.orderTypeId === orderTypeIds.pickup &&
          !selectedStore?.id
        ) {
          const storeObj = res.me.lastCompletedOrder.store;
          setSelectedStore(storeObj);
          setUserOrderMethod(orderMethods.pickup);
          setUserAddress({
            address1: storeObj.mailingAddress,
            city: storeObj.city,
            state: storeObj.state,
            postcode: storeObj.zip,
            lat: storeObj.lat,
            lng: storeObj.lng,
            countryId: parseInt(storeObj.countryId, 10),
          });
        } else {
          const savedLocation = res.me.savedLocations?.locations?.[0] ?? null;
          if (savedLocation && !userAddress?.address1) {
            const { addressDetails } = savedLocation;
            const storeData = {
              lat: addressDetails.lat,
              lng: addressDetails.lng,
              externalId: savedLocation.sourceId,
              orderTypeId: orderTypeIds.delivery,
            };
            const result = await storeRepo.getStores(storeData);
            const { stores } = result.data.storeSearch;
            const storeInDeliveryRangeIndex = stores.findIndex(
              (el) => el.inDeliveryRange === true,
            );
            setUserAddress({ ...addressDetails, savedLocation });
            setSelectedStore(stores[storeInDeliveryRangeIndex]);
            setUserOrderMethod(orderMethods.delivery);
          }
        }
      }
    },
  });

  useEffect(() => {
    if (
      !userToken &&
      !selectedStore?.id &&
      enableDefaultAddressForGuestUsers &&
      guestSelectedStore
    ) {
      setSelectedStore(guestSelectedStore);
      setUserOrderMethod(guestUserOrderMethod);
      setUserAddress(guestUserAddress);
    }
  }, [
    userToken,
    selectedStore,
    enableDefaultAddressForGuestUsers,
    guestSelectedStore,
    guestUserOrderMethod,
    guestUserAddress,
    setSelectedStore,
    setUserOrderMethod,
    setUserAddress,
  ]);

  useEffect(() => {
    setHideNavigationBar(!hideHeaderMenuOnScroll);
  }, [hideHeaderMenuOnScroll]);

  useEffect(() => {
    setHideNavigationBar(false);
  }, [location.pathname, setHideNavigationBar]);

  const mobileMenuPortal = showMobileMenu
    ? ReactDOM.createPortal(
      <MobileMenu
        onClose={() => {
            toggleMobileMenu();
          }}
      />,
        document.body,
      )
    : null;

  const isHomepage = location.pathname === '/';
  const isOnHomepage = isHomepage && useFullscreenCarousel;

  const showNavigation = !hideNavigationBar || (prominentNavigation && isMenuPage);

  const scrolledAwayToastPortal =
    !showNavigation && showNotification
      ? ReactDOM.createPortal(
        <div
          className={styles.scrolledAwayCookieToast}
          data-testid="scrolled-away-cookie-toast"
        >
          <CookieToast
            count={amountAdded}
            imageUrl={productAddedImagePath}
            isMenu={isMenuPage}
          />
        </div>,
          document.body,
        )
      : null;

  return (
    <header
      className={styles.headerWrapper}
      role="banner"
      data-region={getHeaderRegionProps()}
      data-homepage={getHeaderHomepageProps(isOnHomepage)}
    >
      {showNavigation && <RibbonBanner />}
      {showNavigation && (
        <NavigationBar
          isMenuPage={isMenuPage}
          showNotification={showNotification}
          amountAdded={amountAdded}
          productAddedImagePath={productAddedImagePath}
          isHomepage={isHomepage}
          useFullscreenCarousel={useFullscreenCarousel}
        />
      )}
      <ShippingReturnBar
        orderProducts={orderProducts}
        userOrderMethod={userOrderMethod}
        showDeliveryPickupAddressBar={showDeliveryPickupAddressBar}
        userAddress={userAddress}
        showDeliveryPickupWidget={showDeliveryPickupWidget}
        pathname={location.pathname}
      />
      {showHeaderWidget && showDeliveryPickupAddressBar && (
        <div className={styles.widgetWrapper}>
          <DeliveryPickUpWidget
            onClose={hideHeaderWidget}
            addressType={addressType}
            analyticsPageSection={
              headerWidgetSource === "orderNow"
                ? amplitudeDeliveryWidgetPageSection.orderNowNav
                : amplitudeDeliveryWidgetPageSection.nav
            }
          />
        </div>
      )}
      {children}
      {mobileMenuPortal}
      {scrolledAwayToastPortal}
      <BarcodeWidget />
    </header>
  );
};

FullScreenHomepageHeaderComponent.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  showHeaderWidget: PropTypes.bool,
  headerWidgetSource: PropTypes.string,
  showDeliveryPickupAddressBar: PropTypes.bool,
  children: PropTypes.node,
  userToken: PropTypes.string,
  setUserInfo: PropTypes.func.isRequired,
  setUserId: PropTypes.func.isRequired,
  showMobileMenu: PropTypes.bool,
  toggleMobileMenu: PropTypes.func.isRequired,
  hideHeaderWidget: PropTypes.func.isRequired,
  userCart: PropTypes.arrayOf(PropTypes.object),
  selectedStore: PropTypes.shape({}),
  userAddress: PropTypes.shape({}),
  enableDefaultAddressForGuestUsers: PropTypes.bool,
  guestSelectedStore: PropTypes.shape({}),
  guestUserOrderMethod: PropTypes.string,
  guestUserAddress: PropTypes.shape({}),
  setSelectedStore: PropTypes.func.isRequired,
  setUserOrderMethod: PropTypes.func.isRequired,
  setUserAddress: PropTypes.func.isRequired,
  setBetaCustomer: PropTypes.func.isRequired,
  setPermissions: PropTypes.func.isRequired,
  hideHeaderMenuOnScroll: PropTypes.bool,
  userOrderMethod: PropTypes.string,
  showDeliveryPickupWidget: PropTypes.bool,
  orderProducts: PropTypes.arrayOf(PropTypes.object),
  hasActiveOrders: PropTypes.func.isRequired,
  noActiveOrders: PropTypes.func.isRequired,
  activeLoyalty: PropTypes.string,
  order: PropTypes.shape({}),
};

FullScreenHomepageHeaderComponent.defaultProps = {
  showHeaderWidget: false,
  headerWidgetSource: null,
  showDeliveryPickupAddressBar: true,
  children: null,
  userToken: null,
  showMobileMenu: false,
  userCart: [],
  selectedStore: null,
  userAddress: null,
  enableDefaultAddressForGuestUsers: false,
  guestSelectedStore: null,
  guestUserOrderMethod: null,
  guestUserAddress: null,
  hideHeaderMenuOnScroll: true,
  userOrderMethod: null,
  showDeliveryPickupWidget: false,
  orderProducts: [],
  activeLoyalty: null,
  order: null,
};

export const mapStateToProps = (state) => ({
  showHeaderWidget: state.elements.showHeaderWidget,
  headerWidgetSource: state.elements.headerWidgetSource,
  showDeliveryPickupWidget: state.elements.showDeliveryPickupWidget,
  hideHeaderMenuOnScroll: state.elements.hideHeaderMenuOnScroll,
  userToken: state.user.userToken,
  showMobileMenu: state.elements.showMobileMenu,
  userCart: state.user.userCart,
  userOrderMethod: state.user.userOrderMethod,
  orderProducts: state.order?.products || [],
  selectedStore: state.user.selectedStore,
  userAddress: state.user.userAddress,
  enableDefaultAddressForGuestUsers:
    state.address.enableDefaultAddressForGuestUsers,
  guestSelectedStore: state.guestUser.guestSelectedStore,
  guestUserOrderMethod: state.guestUser.guestUserOrderMethod,
  guestUserAddress: state.guestUser.guestUserAddress,
  activeLoyalty: state.loyalty?.activeLoyalty || null,
  order: state.order,
});

export const mapDispatchToProps = (dispatch) => ({
  setUserInfo: (value) => dispatch(userActions.setUserInfo(value)),
  setUserId: (value) => dispatch(userActions.setUserId(value)),
  toggleMobileMenu: () => dispatch(elementsActions.toggleMobileMenu()),
  hideHeaderWidget: () => dispatch(elementsActions.hideHeaderWidget()),
  setSelectedStore: (value) => dispatch(userActions.setSelectedStore(value)),
  setUserOrderMethod: (value) =>
    dispatch(userActions.setUserOrderMethod(value)),
  setUserAddress: (value) => dispatch(userActions.setUserAddress(value)),
  setBetaCustomer: (value) => dispatch(userActions.setBetaCustomer(value)),
  setPermissions: (value) => dispatch(userActions.setPermissions(value)),
  hasActiveOrders: () => dispatch(elementsActions.hasActiveOrders()),
  noActiveOrders: () => dispatch(elementsActions.noActiveOrders()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FullScreenHomepageHeaderComponent),
);
